import { Component, OnInit, ElementRef, ViewChild, Renderer2, OnDestroy } from "@angular/core";
import { MatTabChangeEvent, MatTabGroup } from "@angular/material/tabs";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";

import { Observable, combineLatest, Subscription } from "rxjs";
import { map, pluck } from "rxjs/operators";

import { ConfigurationService } from "../../../../sayso/services/configuration.service";
import { WebCallTopicService } from "../../../../../../common/services/api/resources/webCallTopic/web-call-topic.service";
import { UserSummaryContactService } from "../../../../../../common/services/api/resources/userSummaryContact/user-summary-contact.service";

import { TopicPreviewInfo } from "../../../../../../common/interfaces/sayso-preview";

@Component({
  selector: "onsip-app-topics-list-preview",
  templateUrl: "./app-topics-list-preview.component.html",
  standalone: false
})
export class AppTopicsListPreviewComponent implements OnInit, OnDestroy {
  @ViewChild("tabGroup") tabGroup: MatTabGroup | undefined;
  @ViewChild("saysoPreviewWeb", { read: ElementRef }) saysoPreviewWeb:
    | ElementRef<HTMLElement>
    | undefined;
  @ViewChild("saysoPreviewMobile", { read: ElementRef }) saysoPreviewMobile:
    | ElementRef<HTMLElement>
    | undefined;
  currentPreview: ElementRef<HTMLElement> | undefined;
  previewTopics!: Observable<Array<TopicPreviewInfo>>;
  accentColor!: Observable<string | undefined>;
  buttonColor!: Observable<string | undefined>;
  buttonLocation!: Observable<"right" | "left">;
  gradientOverlay!: Observable<boolean>;
  removedOnsipBranding!: Observable<boolean>;
  showFullWeb!: Observable<boolean>;
  browserWidth = 629; // from design
  mobileWidth!: Observable<number>;

  private unsubscriber = new Subscription();

  constructor(
    private config: ConfigurationService,
    private webCallTopicService: WebCallTopicService,
    private breakpoint: BreakpointObserver,
    private userSummaryContact: UserSummaryContactService,
    private renderer: Renderer2
  ) {}

  ngOnInit() {
    this.buttonColor = this.config.state.pipe(
      map(state => state.orgConfig.buttonColor || undefined)
    );
    this.accentColor = this.config.state.pipe(
      map(state => state.orgConfig.accentColor || undefined)
    );
    this.buttonLocation = this.config.state.pipe(
      map(state => state.orgConfig.horizontalPositionDirection || "right")
    );
    this.gradientOverlay = this.config.state.pipe(
      map(state => state.orgConfig.gradientOverlay || false)
    );
    this.removedOnsipBranding = this.config.state.pipe(
      map(state => state.orgConfig.branding === "removed")
    );
    this.previewTopics = combineLatest([
      this.userSummaryContact.state.pipe(pluck("state")),
      this.webCallTopicService.orgState
    ]).pipe(
      map(([users, webCallTopics]) => {
        function getNameFromId(id: number | undefined) {
          if (!id) return;
          const found = users[id];
          return found && found.contactName;
        }
        function getAvatarUrlFromId(id: number | undefined) {
          if (!id) return;
          const found = users[id];
          return found && found.userAvatarUrl;
        }
        if (webCallTopics) {
          return webCallTopics
            .map(topic => ({
              targetDisplay: topic.configuration?.targetDisplay || "",
              video: topic.configuration?.video || false,
              repName: getNameFromId(topic.webCallTopicReps[0]?.repId),
              repAvatarUrl: getAvatarUrlFromId(topic.webCallTopicReps[0]?.repId)
            }))
            .sort((a, b) => {
              const aName = a.targetDisplay;
              const bName = b.targetDisplay;
              return aName.toUpperCase() >= bName.toUpperCase() ? 0 : -1;
            });
        } else {
          return [];
        }
      })
    );

    this.mobileWidth = this.breakpoint.observe([Breakpoints.XLarge, Breakpoints.Large]).pipe(
      map(state => {
        if (this.tabGroup && this.tabGroup.selectedIndex === 0) {
          this.currentPreview = this.saysoPreviewWeb;
        }
        if (state.breakpoints[Breakpoints.XLarge]) return 400;
        if (state.breakpoints[Breakpoints.Large]) return 360;
        return 0;
      })
    );
    this.showFullWeb = this.breakpoint
      .observe(Breakpoints.XLarge)
      .pipe(map(state => state.matches));

    // HACK to avoid animation when switching between left & right button positions
    this.unsubscriber.add(
      this.buttonLocation.subscribe(() => {
        // in case currentPreview has not been assigned yet. we default to the web preview so
        // set currentPreview to be the webPreviewFrame on the DOM
        if (!this.currentPreview && this.saysoPreviewWeb) {
          this.currentPreview = this.saysoPreviewWeb;
        }

        if (this.currentPreview) {
          const saysoPreview = this.currentPreview.nativeElement.querySelector(".sayso-preview");
          if (
            saysoPreview &&
            saysoPreview.classList.contains("exited") &&
            saysoPreview.classList.contains("sayso-preview-transition")
          ) {
            this.renderer.removeClass(saysoPreview, "sayso-preview-transition");
          }
        }
      })
    );
  }

  ngOnDestroy() {
    this.unsubscriber.unsubscribe();
  }

  changeSelectedPrev(event: MatTabChangeEvent) {
    this.currentPreview = event.index === 0 ? this.saysoPreviewWeb : this.saysoPreviewMobile;
  }
}
